import { useId, useState, type ChangeEvent, type FormEvent } from 'react';
import type { standardLookup } from '../../core/course';
import { addAttachments, collabMaterialHref, moveAttachment, UPLOAD_ACCEPT, uploadFileRefusal, uploadRefusalText, uploadTitle } from '../../core/attachments';
import { MAX_ITEM_MATERIALS, MAX_ITEM_TAGS, MAX_ITEM_TITLE } from '../../core/days';
import type { DayItem, ItemMaterial } from '../../core/types';
import { toApiError, type CollabAccess, type DayItemInput } from '../api';
import { CollabPicker } from './CollabPicker';

/** An attachment as the form shows it: the snapshot, and its group's name when the form knows it. */
type Attached = ItemMaterial & { groupName?: string };
/** Uploads always land in the teacher's own Collab group (R6). */
const MY_PLANNER = 'My Planner';

/**
 * A day item's title, tags, and either its Collab attachments or, with the attachments switch
 * on, today's optional link (R1 to R11, R4 to R6 of day items). With `collab`, there is no link
 * box: the teacher attaches from Collab or uploads a file, and an old item's link shows
 * read-only until removed (R2). Titles, links, and attachments are a teacher's words, so the
 * form stays out of session replay.
 */
export function ItemForm({ heading, initial, course, covering, textOf, busy, submit, collab, onSubmit, onCancel }: {
  heading: string; initial?: DayItem; course: string[]; covering: string[]; textOf: ReturnType<typeof standardLookup>; busy: boolean; submit: string;
  /** Collab through the Planner; absent when the attachments switch is on (R11). */
  collab?: CollabAccess;
  onSubmit(fields: DayItemInput): Promise<boolean>; onCancel(): void;
}) {
  const id = useId();
  const [title, setTitle] = useState(initial?.title ?? '');
  const [link, setLink] = useState(initial?.link ?? '');
  const [tags, setTags] = useState<string[]>(initial?.standardIds ?? []);
  const [materials, setMaterials] = useState<readonly Attached[]>(initial?.materials ?? []);
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const onDay = new Set(covering);
  const groups = [
    { heading: 'On this day', ids: course.filter(s => onDay.has(s)) },
    { heading: 'Other standards in the course', ids: course.filter(s => !onDay.has(s)) },
  ].filter(g => g.ids.length > 0);
  const toggle = (standardId: string) => setTags(t => (t.includes(standardId) ? t.filter(x => x !== standardId) : [...t, standardId]));
  const room = MAX_ITEM_MATERIALS - materials.length;
  const working = busy || uploading !== null;

  const onSave = (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim() || working) return;
    if (!collab) { void onSubmit({ title: title.trim(), link: link.trim() || null, standardIds: tags }); return; }
    // The stored link is left out to keep it, and sent as null once removed (R2).
    const cleared = initial?.link && !link ? { link: null } : {};
    void onSubmit({ title: title.trim(), standardIds: tags, materialIds: materials.map(m => m.materialId), ...cleared });
  };

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !collab) return;
    setUploadError(null);
    const refusal = uploadFileRefusal(file);
    if (refusal) { setUploadError(uploadRefusalText(refusal)); return; }
    setUploading(file.name);
    try {
      const material = await collab.upload(file, uploadTitle(file.name));
      setMaterials(list => addAttachments(list, [{ ...material, groupName: MY_PLANNER }]));
    } catch (failure) {
      setUploadError(uploadRefusalText(toApiError(failure).reason));
    } finally {
      setUploading(null);
    }
  };

  return <>
    <form className="day-item-form planbook-form ph-no-capture" data-ph-no-capture aria-labelledby={`${id}-heading`} onSubmit={onSave}>
      <h3 id={`${id}-heading`}>{heading}</h3>
      <label htmlFor={`${id}-title`}>Title</label>
      {/* The form opens from a cell's control, so the title takes the focus. */}
      <input id={`${id}-title`} type="text" value={title} maxLength={MAX_ITEM_TITLE} required autoFocus onChange={e => setTitle(e.target.value.replace(/[\r\n]/g, ' '))} />
      {!collab && <>
        <label htmlFor={`${id}-link`}>Link (optional)</label>
        <input id={`${id}-link`} type="url" inputMode="url" value={link} placeholder="https://" onChange={e => setLink(e.target.value)} />
      </>}
      {collab && <section className="day-item-attachments" aria-labelledby={`${id}-attachments`}>
        <p id={`${id}-attachments`} className="day-item-attachments-heading">Attachments</p>
        {link && <p className="day-item-old-link">
          <span className="muted">Link </span>
          <a href={link} target="_blank" rel="noopener noreferrer">{link}</a>
          <button type="button" className="text-button" disabled={working} onClick={() => setLink('')}>Remove<span className="sr-only"> link</span></button>
        </p>}
        {materials.length > 0 && <ol className="day-item-materials" aria-label="Attachments">
          {materials.map((m, i) => <li key={m.materialId}>
            <span className="day-item-material-name">
              <a href={collabMaterialHref(m)} target="_blank" rel="noopener noreferrer">{m.title}</a>
              {m.groupName && <small>{m.groupName}</small>}
            </span>
            <span className="day-item-material-tools">
              <button type="button" className="text-button" aria-label={`Move ${m.title} up`} disabled={working || i === 0} onClick={() => setMaterials(l => moveAttachment(l, i, -1))}>↑</button>
              <button type="button" className="text-button" aria-label={`Move ${m.title} down`} disabled={working || i === materials.length - 1} onClick={() => setMaterials(l => moveAttachment(l, i, 1))}>↓</button>
              <button type="button" className="text-button" aria-label={`Remove ${m.title}`} disabled={working} onClick={() => setMaterials(l => l.filter(x => x.materialId !== m.materialId))}>Remove</button>
            </span>
          </li>)}
        </ol>}
        <div className="day-item-attach-actions">
          <button type="button" className="secondary" disabled={working || room === 0} onClick={() => { setUploadError(null); setPicking(true); }}>Attach from Collab</button>
          {/* The input is hidden and its label is the button; the label follows it so focus can show on the label. */}
          <input id={`${id}-file`} className="sr-only" type="file" accept={UPLOAD_ACCEPT} disabled={working || room === 0} onChange={e => void onFile(e)} />
          <label className={`secondary button-link day-item-upload${working || room === 0 ? ' disabled' : ''}`} htmlFor={`${id}-file`}>Upload a file</label>
        </div>
        <p className="muted day-item-attach-hint">
          {room === 0 ? 'Ten attachments is the most an item holds.' : 'PDF, Word, PowerPoint, PNG, JPEG, or text, up to 10 MB. Uploads go to My Planner in Collab.'}
        </p>
        <p className="sr-only" role="status">{uploading ? `Uploading ${uploading}…` : ''}</p>
        {uploading && <p className="muted day-item-uploading" aria-hidden="true">Uploading {uploading}…</p>}
        {uploadError && <p className="error-message" role="alert">{uploadError}</p>}
      </section>}
      <fieldset className="day-item-tag-picker">
        <legend>Standards</legend>
        <p className="muted">{tags.length} of up to {MAX_ITEM_TAGS} chosen.</p>
        {groups.map(g => <div key={g.heading}>
          <p className="day-item-tag-group">{g.heading}</p>
          {g.ids.map(standardId => {
            const s = textOf(standardId);
            const checked = tags.includes(standardId);
            return <label key={standardId} className="day-item-tag-option">
              <input type="checkbox" checked={checked} disabled={!checked && tags.length >= MAX_ITEM_TAGS} onChange={() => toggle(standardId)} />
              <span><strong>{s.code}</strong> {s.statement}</span>
            </label>;
          })}
        </div>)}
      </fieldset>
      <div className="page-actions">
        <button className="primary" type="submit" disabled={working || !title.trim()}>{busy ? 'Saving…' : submit}</button>
        {/* Closing mid-upload would drop the material Collab is storing: Cancel waits for it. */}
        <button className="secondary" type="button" onClick={() => { if (!working) onCancel(); }} disabled={working}>Cancel</button>
      </div>
    </form>
    {/* Outside the form: Enter in the search box must not save the item. */}
    {picking && collab && <CollabPicker search={collab.search} attached={new Set(materials.map(m => m.materialId))} room={room}
      onClose={() => setPicking(false)}
      onAttach={picked => { setMaterials(list => addAttachments(list, picked.map(m => ({ materialId: m.id, groupId: m.groupId, title: m.title, filename: m.filename, groupName: m.groupName })))); setPicking(false); }} />}
  </>;
}
